import React, {
  ComponentProps,
  FormEvent,
  ReactNode,
  useRef,
  useState
} from 'react';
import { Route, useLocation, useNavigate } from 'react-router-dom';
import { AppHeader } from '../../shared/components/AppHeader';
import AppRouter from '../../shared/components/AppRouter/AppRouter';
import { StepRoute } from './StepRoute/StepRoute';
import { OnNext, OnPrev, OnExit } from './types';
import { Step } from './Step/Step';

type WizardProps = {
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  onNext?: OnNext;
  onPrev?: OnPrev;
  onExit?: OnExit;
  children?: ReactNode;
  title: string;
  description?: string | ReactNode;
};

function Wizard(props: WizardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>();

  const childrenArray = React.Children.toArray(props.children);
  const childrenCount = childrenArray.length;

  const routes = childrenArray.map((child: any, index) => {
    if (child.type !== Step) {
      throw new Error('<Wizard /> only accepts <Wizard.Step /> as children.');
    }

    const { children, ...routeProps } = child.props as ComponentProps<
      typeof Step
    >;

    const next = index < childrenCount - 1 ? getStepPath(index + 1) : null;
    const prev = index > 0 ? getStepPath(index - 1) : null;

    let defaultPrevented = false;
    const preventDefault = () => {
      defaultPrevented = true;
    };

    const onNext = async (canContinue: boolean) => {
      setLoading(true);
      // <Wizard.Step onNext={async () => {}} />
      if (routeProps.onNext) {
        await routeProps.onNext({ canContinue, preventDefault });
      }
      // <Wizard onNext={async () => {}} />
      if (props.onNext) {
        await props.onNext({ canContinue, preventDefault });
      }
      if (!defaultPrevented) {
        if (!canContinue) {
          setLoading(false);
          focusFirstInvalidField(formContainerRef);
          return;
        }
        navigate(`./${next}${location.search}`);
      }
      setLoading(false);
    };

    const onPrev = () => {
      if (routeProps.onPrev) {
        routeProps.onPrev({ preventDefault });
      }
      if (props.onPrev) {
        props.onPrev({ preventDefault });
      }

      if (prev && !defaultPrevented) {
        navigate(`./${prev}${location.search}`);
      }
    };

    const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      setLoading(true);
      if (routeProps.onSubmit) {
        await routeProps.onSubmit({ preventDefault });
      }
      setLoading(false);
      if (!defaultPrevented && props.onSubmit) {
        await props.onSubmit(e);
      }
    };

    return (
      <StepRoute
        hideNextNavigation={!next}
        hidePrevNavigation={!prev}
        {...routeProps}
        onNext={next == null ? onSubmit : onNext}
        onPrev={onPrev}
        key={child.key}
        path={getStepPath(index)}
        render={children}
        index={index}
        childrenCount={childrenCount}
        loading={loading}
      />
    );
  });

  if (routes.length === 0) {
    throw new Error('<Wizard /> must contain at least one step.');
  }

  return (
    <div ref={formContainerRef}>
      <AppHeader title={props.title} description={props.description} />
      <AppRouter>
        {routes.map(route => (
          <Route
            key={route.props.path}
            path={route.props.path}
            element={route}
          />
        ))}
      </AppRouter>
    </div>
  );
}

Wizard.Step = Step;

export default Wizard;

function focusFirstInvalidField(
  formContainerRef: React.RefObject<HTMLDivElement>
) {
  return requestAnimationFrame(() => {
    if (!formContainerRef.current) {
      return;
    }
    const invalidField = formContainerRef.current.querySelector(
      "[aria-invalid='true']"
    ) as any;

    if (invalidField && invalidField.focus) {
      invalidField.focus();
    }
  });
}

function getStepPath(index: number) {
  return `${index + 1}`;
}
